import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, Check, Circle } from 'lucide-react';
import { SoulSignalIcon, TreeOfLifeIcon, GoldenSpiralIcon, MetatronIcon, AncientSunIcon, LotusIcon, VesicaIcon, FederationIcon } from './legendary-icons';

// NEW CANONICAL COMPASS POINTS (REFOUND)
const COMPASS_POINT_ICONS = {
  north: SoulSignalIcon,
  northeast: TreeOfLifeIcon,
  east: GoldenSpiralIcon,
  southeast: MetatronIcon,
  south: AncientSunIcon,
  southwest: LotusIcon,
  west: VesicaIcon,
  northwest: FederationIcon
};

// Golden Ratio constant (Phi)
const PHI = 1.618033988749895;

// Generate Golden Spiral points using Fibonacci sequence
const generateGoldenSpiralPath = (centerX, centerY, maxRadius, turns = 3, points = 100) => {
  const pathPoints = [];
  
  for (let i = 0; i <= points; i++) {
    const t = (i / points) * turns * Math.PI * 2;
    // Golden spiral formula: r = a * e^(b*theta) where b = ln(phi) / (pi/2)
    const b = Math.log(PHI) / (Math.PI / 2);
    const a = maxRadius / Math.exp(b * turns * Math.PI * 2);
    const r = a * Math.exp(b * t);
    
    const x = centerX + r * Math.cos(t);
    const y = centerY + r * Math.sin(t);
    
    pathPoints.push({ x, y });
  }
  
  // Convert to SVG path
  if (pathPoints.length === 0) return '';
  
  let path = `M ${pathPoints[0].x} ${pathPoints[0].y}`;
  for (let i = 1; i < pathPoints.length; i++) {
    path += ` L ${pathPoints[i].x} ${pathPoints[i].y}`;
  }
  
  return path;
};

// Rotating Fractal Golden Spiral Orb Component
const GoldenSpiralOrb = ({ cx, cy, size, isAnimating = true }) => {
  const orbRadius = size * 0.12;
  
  return (
    <g>
      {/* Outer glow pulse */}
      <motion.circle
        cx={cx}
        cy={cy}
        r={orbRadius + 8}
        fill="var(--primary)"
        opacity={0.1}
        animate={isAnimating ? {
          scale: [1, 1.3, 1],
          opacity: [0.1, 0.2, 0.1]
        } : {}}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
      />
      
      {/* Orb background with gradient */}
      <defs>
        <radialGradient id="orbGradient" cx="30%" cy="30%" r="70%">
          <stop offset="0%" stopColor="#a8d5ba" stopOpacity="0.9" />
          <stop offset="50%" stopColor="var(--primary)" stopOpacity="0.8" />
          <stop offset="100%" stopColor="#0d2818" stopOpacity="0.9" />
        </radialGradient>
        <filter id="orbGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2" result="glow"/>
          <feMerge>
            <feMergeNode in="glow"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      
      {/* Main orb sphere */}
      <circle
        cx={cx}
        cy={cy}
        r={orbRadius}
        fill="url(#orbGradient)"
        filter="url(#orbGlow)"
      />
      
      {/* Rotating golden spiral group */}
      <motion.g
        animate={isAnimating ? { rotate: 360 } : {}}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        style={{ transformOrigin: `${cx}px ${cy}px` }}
      >
        {/* Primary golden spiral */}
        <path
          d={generateGoldenSpiralPath(cx, cy, orbRadius * 0.85, 2.5, 80)}
          fill="none"
          stroke="rgba(255,255,255,0.7)"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        
        {/* Secondary spiral (offset) */}
        <g style={{ transform: `rotate(180deg)`, transformOrigin: `${cx}px ${cy}px` }}>
          <path
            d={generateGoldenSpiralPath(cx, cy, orbRadius * 0.85, 2.5, 80)}
            fill="none"
            stroke="rgba(255,255,255,0.4)"
            strokeWidth="1"
            strokeLinecap="round"
          />
        </g>
      </motion.g>
      
      {/* Counter-rotating inner spiral */}
      <motion.g
        animate={isAnimating ? { rotate: -360 } : {}}
        transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
        style={{ transformOrigin: `${cx}px ${cy}px` }}
      >
        <path
          d={generateGoldenSpiralPath(cx, cy, orbRadius * 0.5, 2, 60)}
          fill="none"
          stroke="rgba(168,213,186,0.6)"
          strokeWidth="1"
          strokeLinecap="round"
        />
      </motion.g>
      
      {/* Fibonacci circle sequence (fractal detail) */}
      {[1, 2, 3, 5, 8].map((fib, i) => {
        const r = (fib / 8) * orbRadius * 0.3;
        const angle = (i * 137.5 * Math.PI) / 180; // Golden angle
        const x = cx + Math.cos(angle) * orbRadius * 0.4;
        const y = cy + Math.sin(angle) * orbRadius * 0.4;
        return (
          <motion.circle
            key={i}
            cx={x}
            cy={y}
            r={r}
            fill="none"
            stroke="rgba(255,255,255,0.3)"
            strokeWidth="0.5"
            animate={isAnimating ? {
              opacity: [0.2, 0.5, 0.2],
              scale: [0.9, 1.1, 0.9]
            } : {}}
            transition={{
              duration: 2 + i * 0.5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: i * 0.3
            }}
          />
        );
      })}
      
      {/* Central light point */}
      <circle
        cx={cx - orbRadius * 0.25}
        cy={cy - orbRadius * 0.25}
        r={orbRadius * 0.15}
        fill="rgba(255,255,255,0.6)"
      />
      <circle
        cx={cx - orbRadius * 0.3}
        cy={cy - orbRadius * 0.3}
        r={orbRadius * 0.08}
        fill="rgba(255,255,255,0.8)"
      />
    </g>
  );
};

// Elegant Compass Rose SVG Component
export const CompassRose = ({ 
  size = 220, 
  activePoint = 'north', 
  sections = [], 
  onPointClick,
  className = '' 
}) => {
  const [hoveredPoint, setHoveredPoint] = useState(null);
  
  const center = size / 2;
  const outerRadius = size * 0.35;
  const pointRadius = size * 0.42;
  const labelRadius = size * 0.48;
  
  // NEW Compass point labels (REFOUND canonical alignment)
  const compassPoints = [
    { key: 'north', label: 'N', fullLabel: 'Soul', angle: -90, color: '#2f6f3e' },
    { key: 'northeast', label: 'NE', fullLabel: 'Trust', angle: -45, color: '#3d8b4f' },
    { key: 'east', label: 'E', fullLabel: 'Story', angle: 0, color: '#b9935a' },
    { key: 'southeast', label: 'SE', fullLabel: 'Tools', angle: 45, color: '#c98a6b' },
    { key: 'south', label: 'S', fullLabel: 'Launch', angle: 90, color: '#9d3b3b' },
    { key: 'southwest', label: 'SW', fullLabel: 'Culture', angle: 135, color: '#6a4b33' },
    { key: 'west', label: 'W', fullLabel: 'Reflect', angle: 180, color: '#2b6b69' },
    { key: 'northwest', label: 'NW', fullLabel: 'Regen', angle: 225, color: '#4a7c59' },
  ];
  
  const getPointPosition = (angle, radius) => {
    const rad = (angle * Math.PI) / 180;
    return {
      x: center + radius * Math.cos(rad),
      y: center + radius * Math.sin(rad)
    };
  };
  
  const getSectionData = (key) => {
    return sections.find(s => s.key === key) || {};
  };
  
  const getCompletionColor = (percentage) => {
    if (percentage >= 80) return '#2f6f3e';
    if (percentage >= 50) return '#4a7c59';
    if (percentage >= 25) return '#b9935a';
    return 'var(--muted)';
  };

  return (
    <svg 
      width={size} 
      height={size} 
      viewBox={`0 0 ${size} ${size}`} 
      className={className}
      style={{ overflow: 'visible' }}
      data-testid="compass-rose"
    >
      <defs>
        {/* Gradient for the outer ring */}
        <linearGradient id="compassGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="var(--primary)" stopOpacity="0.4" />
          <stop offset="50%" stopColor="var(--accent)" stopOpacity="0.3" />
          <stop offset="100%" stopColor="var(--primary)" stopOpacity="0.4" />
        </linearGradient>
        
        {/* Glow filter */}
        <filter id="pointGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
        
        {/* Soft shadow for depth */}
        <filter id="softShadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="1" stdDeviation="2" floodOpacity="0.2"/>
        </filter>
      </defs>
      
      {/* Outer decorative ring - dashed */}
      <circle 
        cx={center} 
        cy={center} 
        r={outerRadius + 12} 
        fill="none" 
        stroke="var(--primary)" 
        strokeWidth="0.5" 
        opacity="0.15" 
        strokeDasharray="3 6"
      />
      
      {/* Main compass ring */}
      <circle 
        cx={center} 
        cy={center} 
        r={outerRadius} 
        fill="none" 
        stroke="url(#compassGradient)" 
        strokeWidth="2" 
      />
      
      {/* Inner decorative circle */}
      <circle 
        cx={center} 
        cy={center} 
        r={outerRadius * 0.6} 
        fill="none" 
        stroke="var(--primary)" 
        strokeWidth="0.5" 
        opacity="0.2" 
      />
      
      {/* Cardinal direction lines */}
      {[-90, 0, 90, 180].map((angle) => {
        const inner = getPointPosition(angle, outerRadius * 0.45);
        const outer = getPointPosition(angle, outerRadius - 2);
        return (
          <line
            key={`cardinal-${angle}`}
            x1={inner.x}
            y1={inner.y}
            x2={outer.x}
            y2={outer.y}
            stroke="var(--primary)"
            strokeWidth="1.5"
            opacity="0.25"
          />
        );
      })}
      
      {/* Intercardinal direction lines */}
      {[-45, 45, 135, 225].map((angle) => {
        const inner = getPointPosition(angle, outerRadius * 0.55);
        const outer = getPointPosition(angle, outerRadius - 5);
        return (
          <line
            key={`intercardinal-${angle}`}
            x1={inner.x}
            y1={inner.y}
            x2={outer.x}
            y2={outer.y}
            stroke="var(--primary)"
            strokeWidth="0.75"
            opacity="0.15"
          />
        );
      })}
      
      {/* Compass points */}
      {compassPoints.map((point) => {
        const pos = getPointPosition(point.angle, pointRadius);
        const sectionData = getSectionData(point.key);
        const isActive = activePoint === point.key;
        const isHovered = hoveredPoint === point.key;
        const completion = sectionData.completion_percentage || 0;
        const pointSize = isActive ? 14 : isHovered ? 13 : 11;
        
        return (
          <g 
            key={point.key}
            onClick={() => onPointClick?.(point.key)}
            onMouseEnter={() => setHoveredPoint(point.key)}
            onMouseLeave={() => setHoveredPoint(null)}
            style={{ cursor: 'pointer' }}
            data-testid={`compass-point-${point.key}`}
          >
            {/* Active/hover glow */}
            {(isActive || isHovered) && (
              <motion.circle
                cx={pos.x}
                cy={pos.y}
                r={pointSize + 6}
                fill={point.color}
                opacity={0.25}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 0.25 }}
              />
            )}
            
            {/* Completion arc background */}
            <circle
              cx={pos.x}
              cy={pos.y}
              r={pointSize + 2}
              fill="none"
              stroke="var(--muted)"
              strokeWidth={1.5}
              opacity={0.3}
            />
            
            {/* Completion arc */}
            {completion > 0 && (
              <circle
                cx={pos.x}
                cy={pos.y}
                r={pointSize + 2}
                fill="none"
                stroke={getCompletionColor(completion)}
                strokeWidth={1.5}
                strokeDasharray={`${(completion / 100) * 2 * Math.PI * (pointSize + 2)} ${2 * Math.PI * (pointSize + 2)}`}
                strokeDashoffset={2 * Math.PI * (pointSize + 2) * 0.25}
                strokeLinecap="round"
              />
            )}
            
            {/* Point circle */}
            <motion.circle
              cx={pos.x}
              cy={pos.y}
              r={pointSize}
              fill={isActive ? point.color : 'var(--card)'}
              stroke={point.color}
              strokeWidth={isActive ? 2.5 : 1.5}
              filter={isActive ? 'url(#pointGlow)' : undefined}
              animate={{ 
                scale: isActive ? 1 : isHovered ? 1.08 : 1,
              }}
              transition={{ duration: 0.2 }}
            />
            
            {/* Direction label */}
            <text
              x={pos.x}
              y={pos.y}
              textAnchor="middle"
              dominantBaseline="middle"
              fill={isActive ? 'white' : point.color}
              fontSize={isActive ? 9 : 8}
              fontWeight="600"
              fontFamily="'EB Garamond', serif"
            >
              {point.label}
            </text>
          </g>
        );
      })}
      
      {/* Golden Spiral Fractal Orb in Center */}
      <GoldenSpiralOrb cx={center} cy={center} size={size} isAnimating={true} />
    </svg>
  );
};

// Compact Compass Navigation Sidebar
export const CompassNavSidebar = ({ 
  sections = [], 
  activeSection, 
  onSectionClick,
  compassMeta = []
}) => {
  const orderedKeys = ['north', 'northeast', 'east', 'southeast', 'south', 'southwest', 'west', 'northwest'];
  
  const getSectionData = (key) => sections.find(s => s.key === key) || {};
  const getMetaData = (key) => compassMeta.find(m => m.key === key) || {};
  
  const getStageInfo = (percentage) => {
    if (percentage >= 80) return { label: 'Bloom', emoji: '🌸', color: '#2f6f3e' };
    if (percentage >= 50) return { label: 'Bud', emoji: '🌿', color: '#4a7c59' };
    if (percentage >= 25) return { label: 'Sprout', emoji: '🌱', color: '#b9935a' };
    return { label: 'Seed', emoji: '🌰', color: '#6a4b33' };
  };

  return (
    <div className="space-y-1" data-testid="compass-nav-sidebar">
      {orderedKeys.map((key, index) => {
        const section = getSectionData(key);
        const meta = getMetaData(key);
        const isActive = activeSection === key;
        const completion = section.completion_percentage || 0;
        const stage = getStageInfo(completion);
        const IconComponent = COMPASS_POINT_ICONS[key] || Circle;
        
        return (
          <motion.button
            key={key}
            onClick={() => onSectionClick(key)}
            className={`w-full text-left p-2.5 rounded-xl transition-all duration-200 border ${
              isActive 
                ? 'bg-[var(--primary)] text-white shadow-lg border-[var(--primary)]' 
                : 'bg-[var(--card)] hover:bg-[var(--muted)]/50 text-[var(--fg)] border-transparent hover:border-[var(--muted)]'
            }`}
            whileHover={{ x: isActive ? 0 : 3 }}
            whileTap={{ scale: 0.98 }}
            data-testid={`compass-nav-${key}`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div 
                  className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                    isActive ? 'bg-white/20' : ''
                  }`}
                  style={{ 
                    backgroundColor: isActive ? 'rgba(255,255,255,0.2)' : (meta.color || 'var(--primary)') + '15',
                  }}
                >
                  <IconComponent 
                    size={16} 
                    className={isActive ? 'text-white' : ''} 
                    style={{ color: isActive ? 'white' : meta.color || 'var(--primary)' }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`font-medium text-sm truncate ${isActive ? 'text-white' : ''}`}>
                    {meta.title || key}
                  </div>
                  <div className={`text-[10px] truncate ${isActive ? 'text-white/70' : 'text-[var(--fg)]/40'}`}>
                    {meta.subtitle || ''}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <div 
                  className={`w-6 h-1 rounded-full overflow-hidden ${isActive ? 'bg-white/20' : 'bg-[var(--muted)]'}`}
                >
                  <motion.div 
                    className="h-full rounded-full"
                    style={{ backgroundColor: isActive ? 'white' : stage.color }}
                    initial={{ width: 0 }}
                    animate={{ width: `${completion}%` }}
                    transition={{ duration: 0.5 }}
                  />
                </div>
                <ChevronRight size={12} className={`flex-shrink-0 ${isActive ? 'text-white' : 'text-[var(--fg)]/20'}`} />
              </div>
            </div>
          </motion.button>
        );
      })}
    </div>
  );
};

// Sub-elements list for a compass point
export const SubElementsList = ({ 
  subElements = [], 
  subElementsContent = {},
  activeSubElement,
  onSubElementClick 
}) => {
  return (
    <div className="space-y-2 mt-4" data-testid="sub-elements-list">
      {subElements.map((element, index) => {
        const hasContent = subElementsContent[element.key]?.trim();
        const isActive = activeSubElement === element.key;
        
        return (
          <motion.button
            key={element.key}
            onClick={() => onSubElementClick(element.key)}
            className={`w-full text-left p-4 rounded-xl border transition-all duration-200 ${
              isActive 
                ? 'border-[var(--primary)] bg-[var(--primary)]/10 shadow-sm' 
                : hasContent 
                  ? 'border-[var(--success)]/30 bg-[var(--success)]/5 hover:border-[var(--success)]/50' 
                  : 'border-[var(--muted)] bg-[var(--card)] hover:border-[var(--primary)]/30 hover:bg-[var(--muted)]/30'
            }`}
            whileHover={{ x: 2, scale: 1.01 }}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            data-testid={`sub-element-${element.key}`}
          >
            <div className="flex items-start gap-3">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                hasContent ? 'bg-[var(--success)]' : isActive ? 'bg-[var(--primary)]' : 'bg-[var(--muted)]'
              }`}>
                {hasContent ? (
                  <Check size={14} className="text-white" />
                ) : (
                  <span className="text-xs text-white font-medium">{index + 1}</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className={`font-medium text-sm ${
                  isActive ? 'text-[var(--primary)]' : hasContent ? 'text-[var(--success-fg)]' : 'text-[var(--fg)]'
                }`}>
                  {element.title}
                </div>
                <div className="text-xs text-[var(--fg)]/50 mt-1 line-clamp-2 leading-relaxed">
                  {element.guidance}
                </div>
              </div>
            </div>
          </motion.button>
        );
      })}
    </div>
  );
};

export default CompassRose;
